import { useEffect, useState } from "react";
import { onIdTokenChanged, User } from "firebase/auth";

import { auth } from "integrations/firebase/auth";
import { logError } from "shared/services/ErrorReporting";

export const useAuthToken = (authUser?: User | null) => {
  const [value, setValue] = useState<string>();

  useEffect(() => {
    if (!authUser) {
      // clears the auth token when there is no signed-in user before subscribing to token changes; synchronizes to external auth state
      setValue(undefined);
      return;
    }

    let subscribed = true;

    const updateToken = async (firebaseUser: User, retryCount = 0) => {
      const MAX_RETRIES = 2;
      const RETRY_DELAY = 1000;

      try {
        const token = await firebaseUser.getIdToken();

        if (!subscribed) {
          return;
        }

        setValue(token);
        return token;
      } catch (err) {
        if (!subscribed) {
          return;
        }

        if (retryCount < MAX_RETRIES) {
          const delay = RETRY_DELAY * 2 ** retryCount;
          await new Promise((resolve) => {
            // one-shot retry backoff; the timer resolves the awaited promise and cannot leak
            setTimeout(resolve, delay);
          });

          if (!subscribed) {
            return;
          }

          return updateToken(firebaseUser, retryCount + 1);
        }

        logError(err);
        setValue(undefined);
      }
    };

    const unsubscribe = onIdTokenChanged(auth, async (firebaseUser) => {
      if (!subscribed) {
        return;
      }

      if (firebaseUser) {
        await updateToken(firebaseUser);
      } else {
        setValue(undefined);
      }
    });

    return () => {
      subscribed = false;
      unsubscribe();
    };
  }, [authUser]);

  return value;
};
